/**
 * Paste + drag-and-drop attachments for any text input that accepts context (session composer,
 * Capture, New session, and the upcoming bark box / decision threads).
 *
 * The hook only collects local files and previews; sending goes through
 * `Backend.uploadAttachments` → `BrowserArtifacts.prepare` (sealed, then uploaded), never
 * plaintext. Sending code calls `markUploading` around the upload and `markFailed` on failure.
 */
import { ARTIFACT_TRANSPORT_MAX_BYTES } from '@viko/crypto'
import type { AttachmentItem } from '@viko/transcript-ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PendingFile } from './uploads.js'

export const LARGE_TEXT_BYTES = 32 * 1024
const MAX_FILES = 20

export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export interface AttachmentNotice {
  id: string
  text: string
}

export interface AttachedFile extends PendingFile {
  status: 'ready' | 'uploading' | 'error'
  error?: string
}

export interface AttachmentInputOptions {
  disabled?: boolean
  /** The hub said file storage is off: files are refused with an explanation. */
  storageOff?: boolean
  maxFileBytes?: number
  maxFiles?: number
  maxTotalBytes?: number
  /** Offer pasted text above this many bytes as a `.txt` attachment (needs `insertText`). */
  largeTextBytes?: number
  insertText?: (text: string) => void
}

/** Files (and a folder count) from a drop or paste; folders can't be read, so they're counted. */
export function readTransfer(dt: DataTransfer | null | undefined): {
  files: File[]
  folders: number
} {
  const files: File[] = []
  let folders = 0
  if (!dt) return { files, folders }
  const items = Array.from(dt.items ?? [])
  for (const it of items) {
    if (it.kind !== 'file') continue
    const entry = (
      it as DataTransferItem & { webkitGetAsEntry?: () => { isDirectory: boolean } | null }
    ).webkitGetAsEntry?.()
    if (entry?.isDirectory) {
      folders++
      continue
    }
    const f = it.getAsFile()
    if (f) files.push(f)
  }
  // Some sources (older browsers, synthetic events) only fill `files`.
  if (!files.length && !folders) files.push(...Array.from(dt.files ?? []))
  return { files, folders }
}

const GENERIC = /^image\.(png|jpe?g|gif|webp)$/i
let pasteSeq = 0
/** Clipboard screenshots all arrive as `image.png`: give each a distinct, readable name. */
function nameClipboardFile(f: File): File {
  if (f.name && !GENERIC.test(f.name)) return f
  const ext = (f.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg')
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  return new File([f], `pasted-${stamp}${pasteSeq++ ? `-${pasteSeq}` : ''}.${ext}`, {
    type: f.type,
    lastModified: f.lastModified,
  })
}

const hasFiles = (dt: DataTransfer | null | undefined) =>
  !!dt && Array.from(dt.types ?? []).includes('Files')

export function useAttachmentInput(o: AttachmentInputOptions = {}) {
  const maxFileBytes = o.maxFileBytes ?? ARTIFACT_TRANSPORT_MAX_BYTES
  const maxFiles = o.maxFiles ?? MAX_FILES
  const [files, setFiles] = useState<AttachedFile[]>([])
  const [notices, setNotices] = useState<AttachmentNotice[]>([])
  const [dragging, setDragging] = useState(false)
  const [largePaste, setLargePaste] = useState<string | null>(null)
  const depth = useRef(0)
  const live = useRef(files)
  live.current = files
  const opts = useRef(o)
  opts.current = o

  useEffect(
    () => () => {
      for (const f of live.current) if (f.url) URL.revokeObjectURL(f.url)
    },
    [],
  )

  const notify = useCallback((text: string) => {
    setNotices((n) => [...n.filter((x) => x.text !== text), { id: crypto.randomUUID(), text }])
  }, [])

  const add = useCallback(
    (list: Iterable<File> | ArrayLike<File> | null | undefined, extra?: { folders?: number }) => {
      const { disabled, storageOff, maxTotalBytes } = opts.current
      const incoming = list ? Array.from(list as ArrayLike<File>) : []
      if (extra?.folders)
        notify(
          `Folders can't be attached (${extra.folders}). Zip the folder, or select the files inside it.`,
        )
      if (!incoming.length) return
      if (disabled) return
      if (storageOff)
        return notify(
          'File storage is not enabled on this Viko server yet, so files can’t be attached.',
        )
      const room = Math.max(0, maxFiles - live.current.length)
      if (incoming.length > room)
        notify(
          `Only ${maxFiles} files can be attached at once; ${incoming.length - room} were skipped. Send these first, then add the rest.`,
        )
      let total = live.current.reduce((n, f) => n + f.file.size, 0)
      const next: AttachedFile[] = []
      for (const raw of incoming.slice(0, room)) {
        const file = nameClipboardFile(raw)
        total += file.size
        let error: string | undefined
        if (file.size === 0)
          error = 'This file is empty (or is a folder). Remove it and attach a real file.'
        else if (file.size > maxFileBytes)
          error = `Too large (${formatSize(file.size)}); the limit is ${formatSize(maxFileBytes)}. Remove it and attach a smaller file or a zip.`
        else if (maxTotalBytes && total > maxTotalBytes)
          error = `Adds up to ${formatSize(total)}; the total limit is ${formatSize(maxTotalBytes)}. Remove some attachments.`
        next.push({
          id: crypto.randomUUID(),
          file,
          name: file.name || 'attachment',
          url: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
          status: error ? 'error' : 'ready',
          ...(error ? { error } : {}),
        })
      }
      setFiles((f) => [...f, ...next])
    },
    [maxFiles, maxFileBytes, notify],
  )

  const remove = useCallback((id: string) => {
    setFiles((f) => {
      const gone = f.find((x) => x.id === id)
      if (gone?.url) URL.revokeObjectURL(gone.url)
      return f.filter((x) => x.id !== id)
    })
  }, [])

  const clear = useCallback(() => {
    for (const f of live.current) if (f.url) URL.revokeObjectURL(f.url)
    setFiles([])
  }, [])

  /** Put files back after a failed send (they keep their previews). */
  const restore = useCallback((back: AttachedFile[], error?: string) => {
    setFiles(
      back.map((f) => ({
        ...f,
        status: error ? ('error' as const) : ('ready' as const),
        ...(error ? { error } : {}),
      })),
    )
  }, [])

  const markUploading = useCallback((on: boolean) => {
    setFiles((f) =>
      f.map((x) => {
        if (x.status === 'error') return x
        return { ...x, status: on ? 'uploading' : 'ready' }
      }),
    )
  }, [])

  const markFailed = useCallback((message: string) => {
    setFiles((f) => f.map((x) => ({ ...x, status: 'error', error: message })))
  }, [])

  /** Clear per-file errors that came from a failed send so the user can retry. */
  const retryable = useCallback(() => {
    setFiles((f) =>
      f.map((x) =>
        x.status === 'error' && x.file.size <= maxFileBytes && x.file.size > 0
          ? { ...x, status: 'ready', error: undefined }
          : x,
      ),
    )
  }, [maxFileBytes])

  const onPaste = useCallback(
    (e: { clipboardData: DataTransfer | null; preventDefault: () => void }) => {
      const { files: pasted, folders } = readTransfer(e.clipboardData)
      if (pasted.length || folders) {
        e.preventDefault()
        add(pasted, { folders })
        return
      }
      const { largeTextBytes, insertText } = opts.current
      const text = e.clipboardData?.getData('text/plain') ?? ''
      if (largeTextBytes && insertText && new Blob([text]).size > largeTextBytes) {
        e.preventDefault()
        setLargePaste(text)
      }
    },
    [add],
  )

  const resolveLargePaste = useCallback(
    (as: 'text' | 'file' | 'dismiss') => {
      const text = largePaste
      setLargePaste(null)
      if (text == null || as === 'dismiss') return
      if (as === 'text') opts.current.insertText?.(text)
      else add([new File([text], 'pasted-text.txt', { type: 'text/plain' })])
    },
    [add, largePaste],
  )

  const dropProps = useMemo(
    () => ({
      onDragEnter: (e: React.DragEvent) => {
        if (!hasFiles(e.dataTransfer)) return
        e.preventDefault()
        depth.current++
        setDragging(true)
      },
      onDragOver: (e: React.DragEvent) => {
        if (!hasFiles(e.dataTransfer)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      },
      onDragLeave: (e: React.DragEvent) => {
        if (!hasFiles(e.dataTransfer)) return
        depth.current = Math.max(0, depth.current - 1)
        if (!depth.current) setDragging(false)
      },
      onDrop: (e: React.DragEvent) => {
        if (!hasFiles(e.dataTransfer)) return
        e.preventDefault()
        depth.current = 0
        setDragging(false)
        const { files: dropped, folders } = readTransfer(e.dataTransfer)
        add(dropped, { folders })
      },
    }),
    [add],
  )

  const items: AttachmentItem[] = files.map((f) => ({
    id: f.id,
    name: f.name,
    ...(f.url ? { url: f.url } : {}),
    mime: f.file.type,
    size: f.file.size,
    status: f.status,
    ...(f.error ? { error: f.error } : {}),
  }))

  return {
    files,
    items,
    add,
    remove,
    clear,
    restore,
    markUploading,
    markFailed,
    retryable,
    notices,
    dismissNotice: (id: string) => setNotices((n) => n.filter((x) => x.id !== id)),
    largePaste,
    resolveLargePaste,
    dragging,
    onPaste,
    dropProps,
    /** False while any chip is in an error state: sending would drop or fail that file. */
    canSend: !files.some((f) => f.status === 'error' || f.status === 'uploading'),
  }
}

export type AttachmentInput = ReturnType<typeof useAttachmentInput>
